/**
 * "Configure sua empresa" — a lista do onboarding na Visão Geral (ADR-0104).
 *
 * Só para quem administra a empresa (`empresa:configurar`). Cada item se
 * marca SOZINHO, pelo dado real que o servidor encontra — ninguém marca à
 * mão, então a lista nunca mente. É da empresa, não da pessoa: o que um
 * administrador faz aparece feito para todos, e a seção some para todos ao
 * chegar a 100%.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiCheckCircle, FiChevronRight, FiCircle, FiMinusCircle, FiRefreshCw } from "react-icons/fi";
import { Link } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import { useAuth } from "../../context/AuthContext";
import {
  PERMISSAO_CONFIGURAR_EMPRESA,
  desfazerDispensa,
  lerEstadoOnboarding,
} from "../../services/onboarding";
import { formatarDataLocal } from "../../utils/dinheiro";
import "../../styles/canonical/onboarding.css";

import DialogoNaoUsoIsto from "./DialogoNaoUsoIsto";

const SELO = {
  feito: "Feito",
  pendente: "Pendente",
  dispensado: "Não usa",
};

const MARCA = {
  feito: FiCheckCircle,
  pendente: FiCircle,
  dispensado: FiMinusCircle,
};

function contarFeitos(itens) {
  return itens.filter((item) => item.situacao !== "pendente").length;
}

function Item({ item, onDispensar, onDesfazer }) {
  const Marca = MARCA[item.situacao] || FiCircle;
  const dispensa = item.dispensa;
  return (
    <li className={`onb-item is-${item.situacao}`}>
      <span className="onb-item__marca" aria-hidden="true">
        <Marca size={18} />
      </span>
      <div className="onb-item__texto">
        <div className="onb-item__linha">
          <span className="onb-item__titulo">{item.titulo}</span>
          <span className={`onb-selo is-${item.situacao}`}>{SELO[item.situacao]}</span>
        </div>
        {item.situacao === "pendente" && <p className="onb-item__desc">{item.descricao}</p>}
        {item.situacao === "dispensado" && dispensa && (
          <p className="onb-item__desc">
            Motivo: {dispensa.motivo}
            {dispensa.por ? ` — ${dispensa.por}` : ""}
            {dispensa.em ? `, em ${formatarDataLocal(dispensa.em)}` : ""}
          </p>
        )}
      </div>
      {item.situacao !== "feito" && (
        <div className="onb-item__acoes">
          {item.situacao === "pendente" && (
            <Link className="onb-btn" to={item.link}>
              Configurar <FiChevronRight size={14} aria-hidden="true" />
            </Link>
          )}
          {item.situacao === "pendente" && item.dispensavel && (
            <button type="button" className="onb-btn" onClick={() => onDispensar(item)}>
              Não uso isto
            </button>
          )}
          {item.situacao === "dispensado" && (
            <AsyncButton
              variant="secondary"
              onClick={() => onDesfazer(item)}
              silentError
              successMessage="O item voltou para a lista."
              aria-label={`Desfazer: voltar a usar ${item.titulo}`}
            >
              Desfazer
            </AsyncButton>
          )}
        </div>
      )}
    </li>
  );
}

export default function ConfigureSuaEmpresa() {
  const { request, hasAllPermissions } = useAuth();
  const podeConfigurar = hasAllPermissions([PERMISSAO_CONFIGURAR_EMPRESA]);
  const [estado, setEstado] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [dispensando, setDispensando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      setEstado(await lerEstadoOnboarding(request));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar a lista agora.");
    } finally {
      setCarregando(false);
    }
  }, [request]);

  useEffect(() => {
    if (podeConfigurar) carregar();
  }, [podeConfigurar, carregar]);

  const desfazer = useCallback(
    async (item) => {
      try {
        setEstado(await desfazerDispensa(request, item.chave));
      } catch (err) {
        setErro(err?.message || "Não foi possível desfazer agora.");
        throw err;
      }
    },
    [request],
  );

  if (!podeConfigurar) return null;
  // Lista completa: a seção some (para todos os administradores).
  if (estado?.completo) return null;

  if (!estado) {
    return (
      <section className="onb-lista" aria-busy={carregando} aria-labelledby="onb-titulo">
        <h2 id="onb-titulo" className="onb-titulo">Configure sua empresa</h2>
        {erro ? (
          <div className="onb-aviso" role="alert">
            {erro}
            <button type="button" className="onb-btn" onClick={carregar}>
              <FiRefreshCw size={14} aria-hidden="true" /> Tentar de novo
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 12 }} aria-label="Carregando a lista">
            <div className="onb-esqueleto" style={{ width: "60%" }} />
            <div className="onb-esqueleto" style={{ width: "85%" }} />
            <div className="onb-esqueleto" style={{ width: "40%" }} />
          </div>
        )}
      </section>
    );
  }

  const fracao = estado.total ? estado.feitos / estado.total : 0;
  return (
    <section className="onb-lista" aria-labelledby="onb-titulo">
      {/* O tour destaca o cabeçalho (título e progresso): a lista inteira não cabe na tela. */}
      <div className="onb-cabeca" data-tour="configure-empresa">
        <div className="onb-cabeca__texto">
          <h2 id="onb-titulo" className="onb-titulo">Configure sua empresa</h2>
          <p className="onb-sub">
            O que falta para deixar a empresa pronta. Cada item se marca sozinho quando o sistema
            encontra a configuração feita — e a lista some quando chegar a 100%.
          </p>
        </div>
        <div className="onb-progresso">
          <span className="onb-progresso__num">
            {estado.feitos} de {estado.total} feitos
          </span>
          <div
            className="onb-barra"
            role="progressbar"
            aria-label="Progresso da configuração da empresa"
            aria-valuemin={0}
            aria-valuemax={estado.total}
            aria-valuenow={estado.feitos}
          >
            <div className="onb-barra__cheia" style={{ transform: `scaleX(${fracao})` }} />
          </div>
        </div>
      </div>

      {erro && (
        <div className="onb-aviso" role="alert">
          {erro}
        </div>
      )}

      <div className="onb-grupos">
        {estado.grupos.map((grupo) => (
          <div className="onb-grupo" key={grupo.chave}>
            <h3 className="onb-grupo__titulo">
              {grupo.titulo}
              <span className="onb-grupo__conta">
                {contarFeitos(grupo.itens)}/{grupo.itens.length}
              </span>
            </h3>
            <ul className="onb-itens">
              {grupo.itens.map((item) => (
                <Item key={item.chave} item={item} onDispensar={setDispensando} onDesfazer={desfazer} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      {dispensando && (
        <DialogoNaoUsoIsto
          item={dispensando}
          onFechar={() => setDispensando(null)}
          onDispensado={(novo) => {
            setEstado(novo);
            setDispensando(null);
          }}
        />
      )}
    </section>
  );
}
